import React, { Fragment, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Spin,
  Upload,
} from 'antd'
import {
  uploadCompanyEO,
  uploadAppraiserEO,
} from '@actions/currentUser'
import {
  selectAppraiserUpdateStatus,
  selectEOStatus,
  selectEOInformation,
} from '@reducers/currentUser/currentUserSelectors'
import { SUCCEEDED, ERROR } from '@constants/fetchStatuses'
import { UploadOutlined } from '@ant-design/icons'
import { COMPANY_MEMBER } from '@components/Signup/constants'
import useSignupAnalyticsTracker from '@components/Signup/SegmentAnalytics/useSignupAnalyticsTracker'
import './EOInsurance.scss'

const formItemLayout = {
  labelCol: { span: 24 },
}

const retroOtherOptionsSelected = (priorActs) => {
  const otherOptions = ['n/a', 'unlimited', 'full']
  return otherOptions.includes(priorActs)
}

const EOInsurance = ({
  eoFile,
  companyID,
  currentUserID,
  setEOFile,
  setUploadingEO,
  uploadingEO,
  userType,
  setPreviouslyUploadedEOFile,
}) => {
  const track = useSignupAnalyticsTracker()
  const dispatch = useDispatch()

  const [fileBeingUploaded, setFileBeingUploaded] = useState(null)
  const eoStatus = useSelector(selectEOStatus)
  const appraiserUpdateStatus = useSelector(selectAppraiserUpdateStatus)
  const [useCompanyEO, setUseCompanyEO] = useState(true)

  const {
    aggregate_limit,
    claim_limit,
    expiration_date,
    insurance_company,
    prior_acts,
  } = useSelector(selectEOInformation)

  const [retroDateOptions, setRetroDateOptions] = useState(
    retroOtherOptionsSelected(prior_acts)
      ? 'other'
      : 'date'
  )

  const [priorActs, setPriorActs] = useState(
    retroDateOptions === 'other'
      ? prior_acts
      : prior_acts
        ? moment(prior_acts)
        : ''
  )

  useEffect(() => {
    if (uploadingEO && (eoStatus === SUCCEEDED || appraiserUpdateStatus === SUCCEEDED)) {
      setEOFile(fileBeingUploaded)
      setUploadingEO(false)
    } else if (uploadingEO && (eoStatus === ERROR || appraiserUpdateStatus === ERROR)) {
      setUploadingEO(false)
    }
  }, [
    appraiserUpdateStatus,
    eoStatus,
    fileBeingUploaded,
    setEOFile,
    setUploadingEO,
    uploadingEO,
  ])

  const disabledDate = current => {
    // Can not select days before today and today
    return current && current <= moment().endOf('day')
  }

  const handleBeforeUpload = file => {
    track({ eventName: 'Account Setup Upload E&O Clicked' })
    setFileBeingUploaded(file)
    setUploadingEO(true)
    if (userType === COMPANY_MEMBER) {
      dispatch(uploadAppraiserEO(file, currentUserID))
    } else {
      dispatch(uploadCompanyEO(file, companyID))
    }
  }

  const UploadEO = ({ setPreviouslyUploadedEOFile, uploading }) => (
    <Form.Item
      labelCol={{ span: 24 }}
      name='eo_file'
      rules={[
        () => ({
          validator() {
            if (!eoFile) {
              return Promise.reject(new Error('Upload E&O Insurance PDF'))
            }
            return Promise.resolve()
          },
        }),
      ]}
    >
      <Upload
        accept='.pdf, application/pdf'
        fileList={eoFile ? [eoFile] : []}
        onRemove={() => {
          setEOFile(null)
          setPreviouslyUploadedEOFile(false)
        }}
        beforeUpload={handleBeforeUpload}
        customRequest={() => null}
      >
        <Button
          className='upload-button'
          icon={uploading ? null : <UploadOutlined />}
          type='default'
          disabled={uploading}
        >
          {uploading ? <Spin/> : 'Upload E&O PDF'}
        </Button>
      </Upload>
    </Form.Item>
  )

  return (
    <Card className='tax-information'>
      <h3>{'E&O Insurance'}</h3>
      {userType === COMPANY_MEMBER
        ? (
        <Fragment>
          <Form.Item
            label={"Are you covered by your company's E&O insurance?"}
            name='use_company_eo'
            rules={[
              {
                required: true,
                message: 'This question is required.',
              },
            ]}
          >
            <Radio.Group
              onChange={(e) => setUseCompanyEO(e.target.value)}
              value={useCompanyEO}
              className='eo-radio-group'
            >
                <Radio value={true}>Yes, I am covered</Radio>
                <Radio value={false}>No, I am providing my own</Radio>
            </Radio.Group>
          </Form.Item>

          {!useCompanyEO && <UploadEO
            setPreviouslyUploadedEOFile={setPreviouslyUploadedEOFile}
            uploading={uploadingEO}
          />}
        </Fragment>
          )
        : (
        <Fragment>
          <Form.Item
            {...formItemLayout}
            label='Insurance Company'
            name='eo_insurance_company'
            initialValue={insurance_company}
            rules={[
              {
                required: true,
                message: 'Select an Insurance Company',
              },
            ]}
          >
            <Input />
          </Form.Item>

          <UploadEO
            setPreviouslyUploadedEOFile={setPreviouslyUploadedEOFile}
            uploading={uploadingEO}
          />

          <Form.Item
            label='Expiration Date'
            labelCol={{ span: 24 }}
            name='eo_expiration_date'
            initialValue={expiration_date ? moment(expiration_date) : null}
            rules={[
              {
                required: true,
                message: 'Select an expiration date',
              },
            ]}
          >
            <DatePicker disabledDate={disabledDate} placeholder='YYYY-MM-DD'/>
          </Form.Item>

          <Form.Item
            label="Prior Acts or Retroactive Date"
            name="eo_prior_acts"
            labelCol={{ span: 24 }}
            required={true}
            >
            <Select
            className='retro-dropdown'
            defaultValue={retroDateOptions}
            onChange={(value) => {
              setPriorActs('')
              setRetroDateOptions(value)
            }}
            placeholder="Select Option"
            style={{ maxWidth: '210px' }}
            value={retroDateOptions}
            options={[
              {
                label: 'Date',
                value: 'date',
              },
              {
                label: 'Other',
                value: 'other',
              },
            ]}
            />
          </Form.Item>


          {retroDateOptions === 'date'
            ? (
            <Form.Item
              initialValue={priorActs}
              labelCol={{ span: 12 }}
              name="eo_prior_acts_date"
              rules={[
                {
                  required: true,
                  message: 'Select an option or a date',
                },
              ]}
              >
                <DatePicker data-testid='prior-acts-date' />
            </Form.Item>
              )
            : (
            <Form.Item
              initialValue={priorActs}
              labelCol={{ span: 12 }}
              name="eo_prior_acts_other"
              rules={[
                {
                  required: true,
                  message: 'Select an option or a date',
                },
              ]}
            >
              <Radio.Group
                onChange={(e) => {
                  setPriorActs(e.target.value)
                }}
                name="eo_prior_acts"
                className='eo-radio-group'
              >
                <Radio value={'n/a'}>N/A</Radio>
                <Radio value={'unlimited'}>Unlimited</Radio>
                <Radio value={'full'}>Full</Radio>
              </Radio.Group>
            </Form.Item>
              )}

          <Form.Item
            {...formItemLayout}
            label='Per Claim Limit'
            name='eo_claim_limit'
            initialValue={claim_limit}
            rules={[
              {
                required: true,
                message: 'Select a limit',
              },
            ]}
          >
            <InputNumber
              className='insurance-input-number'
              name='eo_claim_limit'
              controls={false}
              min='0'
              addonAfter='$'
            />
          </Form.Item>

          <Form.Item
            {...formItemLayout}
            label='Aggregate Limit'
            name='eo_aggregate_limit'
            initialValue={aggregate_limit}
            rules={[
              {
                required: true,
                message: 'Select a limit',
              },
            ]}
          >
            <InputNumber
              name='eo_aggregate_limit'
              className='insurance-input-number'
              controls={false}
              min='0'
              addonAfter='$'
            />
          </Form.Item>
        </Fragment>
          )}
    </Card>
  )
}

export default EOInsurance
